import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Pagination,
  Stack,
  Tooltip,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SearchIcon from '@mui/icons-material/Search'
import {
  kycService,
  driverService,
  vehicleService,
  VerificationRequestDto,
  KycSubjectType,
  DriverDto,
  VehicleDto,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { getDriverDisplayName, getVehicleDisplayName } from '../components/SubjectDetailPanel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import VerificationReviewDialog from '../components/VerificationReviewDialog'
import PageRefreshButton from '../components/PageRefreshButton'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'

function SubjectNameCell({
  subjectType,
  subjectId,
}: {
  subjectType: KycSubjectType
  subjectId: string
}) {
  const { data } = useQuery({
    queryKey: [subjectType === 'DRIVER' ? 'driver' : 'vehicle', subjectId],
    queryFn: (): Promise<any> =>
      subjectType === 'DRIVER'
        ? driverService.getById(subjectId)
        : vehicleService.getById(subjectId),
    staleTime: 5 * 60 * 1000,
  })
  const name = useMemo(() => {
    if (!data) return null
    if (subjectType === 'DRIVER') return getDriverDisplayName(data as DriverDto)
    return getVehicleDisplayName(data as VehicleDto)
  }, [data, subjectType])
  return (
    <Tooltip title={subjectId}>
      <span>{name || `${subjectId.substring(0, 8)}…`}</span>
    </Tooltip>
  )
}

export default function PendingVerificationsPage() {
  const [page, setPage] = useState(0)
  const [size] = useState(20)
  const [selectedVerificationId, setSelectedVerificationId] = useState<string | null>(null)
  const [selectedVerification, setSelectedVerification] = useState<VerificationRequestDto | null>(
    null
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [subjectTypeFilter, setSubjectTypeFilter] = useState<'ALL' | KycSubjectType>('ALL')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [search, setSearch] = useState('')
  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['kyc-pending', page, size],
    queryFn: () => kycService.getPending(page, size),
  })

  const verifyKycMutation = useMutation({
    mutationFn: (verification: VerificationRequestDto) =>
      kycService.verify(verification.id, verification, ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
      toast.success('KYC verified successfully.')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to verify KYC'),
  })

  const allRows: VerificationRequestDto[] = useMemo(
    () => data?.content ?? data ?? [],
    [data]
  )

  const filteredRows = useMemo(() => {
    return allRows.filter((v) => {
      if (subjectTypeFilter !== 'ALL' && v.subjectType !== subjectTypeFilter) return false
      if (overdueOnly) {
        const due = v.dueDate ? new Date(v.dueDate).getTime() : 0
        if (!due || due > Date.now()) return false
      }
      if (search) {
        const s = search.toLowerCase()
        if (
          !v.id.toLowerCase().includes(s) &&
          !v.subjectId.toLowerCase().includes(s) &&
          !(v.accountId || '').toLowerCase().includes(s)
        ) {
          return false
        }
      }
      return true
    })
  }, [allRows, subjectTypeFilter, overdueOnly, search])

  const handleOpen = (verification: VerificationRequestDto) => {
    setSelectedVerificationId(verification.id)
    setSelectedVerification(verification)
    setDialogOpen(true)
  }

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1)
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2, flex: 1, minHeight: 0 }}>
        <TableSkeleton rows={8} columns={8} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ErrorCard
          title="Failed to load KYC queue"
          message={(error as Error)?.message}
          onRetry={() => refetch()}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Subject type"
            value={subjectTypeFilter}
            onChange={(e) => setSubjectTypeFilter(e.target.value as any)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="ALL">All types</MenuItem>
            <MenuItem value="DRIVER">{getLocalizedLabel('kycSubjectType', 'DRIVER') || 'Driver'}</MenuItem>
            <MenuItem value="VEHICLE">{getLocalizedLabel('kycSubjectType', 'VEHICLE') || 'Vehicle'}</MenuItem>
          </TextField>
          <TextField
            size="small"
            placeholder="Search by id / subject / account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
              />
            }
            label="Overdue only"
          />
          <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table sx={{ height: filteredRows.length === 0 ? '100%' : undefined }}>
          <TableHead>
            <TableRow>
              <ResizableTableCell>Subject</ResizableTableCell>
              <ResizableTableCell>Type</ResizableTableCell>
              <ResizableTableCell>Account</ResizableTableCell>
              <ResizableTableCell>Status</ResizableTableCell>
              <ResizableTableCell>Due / Created</ResizableTableCell>
              <ResizableTableCell>Assigned</ResizableTableCell>
              <ResizableTableCell>AI Score</ResizableTableCell>
              <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                  <EmptyState
                    title="No verifications match the current filters"
                    description="Adjust the subject-type, overdue, or search filters above."
                  />
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((verification: VerificationRequestDto) => {
              const dueDate = verification.dueDate ? new Date(verification.dueDate) : null
              const isOverdue = dueDate ? dueDate.getTime() < Date.now() : false
              return (
                <TableRow
                  key={verification.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOpen(verification)}
                >
                  <TableCell>
                    <SubjectNameCell
                      subjectType={verification.subjectType}
                      subjectId={verification.subjectId}
                    />
                  </TableCell>
                  <TableCell>
                    {getLocalizedLabel('kycSubjectType', verification.subjectType) ||
                      verification.subjectType}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={verification.accountId}>
                      <span>{verification.accountId.substring(0, 8)}…</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getLocalizedLabel('kycVerificationStatus', verification.status)}
                      color={
                        verification.status === 'VERIFIED'
                          ? 'success'
                          : verification.status === 'REJECTED'
                          ? 'error'
                          : 'warning'
                      }
                      size="small"
                    />
                    {verification.requiresManualReview && (
                      <Chip
                        label="Manual"
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {dueDate ? (
                      <Tooltip title={`Created ${new Date(verification.createdAt).toLocaleString()}`}>
                        <Chip
                          label={dueDate.toLocaleDateString()}
                          color={isOverdue ? 'error' : 'default'}
                          size="small"
                          variant={isOverdue ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(verification.createdAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {verification.assignedTo ? (
                      <Tooltip title={verification.assignedTo}>
                        <span>{verification.assignedTo.substring(0, 8)}…</span>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {typeof verification.autoVerificationScore === 'number'
                      ? verification.autoVerificationScore.toFixed(2)
                      : '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Review details">
                        <IconButton size="small" onClick={() => handleOpen(verification)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {verification.status === 'PENDING' && (
                        <Tooltip title="Quick verify (no notes)">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            disabled={verifyKycMutation.isPending}
                            onClick={() => verifyKycMutation.mutate(verification)}
                          >
                            Verify
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {data?.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={data.totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <VerificationReviewDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
          setSelectedVerificationId(null)
          setSelectedVerification(null)
        }}
        verificationId={selectedVerificationId || undefined}
        verificationHint={selectedVerification || undefined}
        onActionComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['kyc-pending'] })
        }}
      />
    </Box>
  )
}
